/**
 * Project share links (migration 00542): an admin's Share button on a project
 * makes a link /join/<token>; whoever signs up or signs in through it is
 * approved with access to that project only (app_access.allowed_projects).
 * The normal sign-up and approval path is unchanged.
 *
 * The token lives only in the link: the table keeps its SHA-256. The browser
 * holds a clicked token in localStorage through sign-up, email confirmation
 * and sign-in, and <AccessGate> redeems it once the person is signed in
 * (/api/access/redeem-share, which checks the link with the service role).
 */
import { supabase } from '@/lib/supabase'
import { isEntraAuthEnabled } from '@/lib/entraAuth'

/** How long a new link works. */
export const SHARE_LINK_DAYS = 3

const PENDING_KEY = 'xpm.pendingShareToken'
const NOTICE_KEY = 'xpm.shareNotice'
const JOIN_PATH = /^\/join\/([A-Za-z0-9_-]{20,200})\/?$/

export interface ShareLink {
  id: string
  createdBy: string | null
  createdAt: string
  expiresAt: string
  uses: number
  lastUsedAt: string | null
  lastUsedBy: string | null
}

function base64url(bytes: Uint8Array): string {
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export async function sha256Hex(v: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(v))
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** Make a link for a project (admins only, by RLS). The URL is shown once: only its hash is stored. */
export async function createShareLink(projectId: string, createdBy: string | null): Promise<{ url: string; expiresAt: string }> {
  const token = base64url(crypto.getRandomValues(new Uint8Array(32)))
  const expiresAt = new Date(Date.now() + SHARE_LINK_DAYS * 24 * 3600 * 1000).toISOString()
  const { error } = await supabase.from('project_share_links').insert({
    id: `psl_${base64url(crypto.getRandomValues(new Uint8Array(9)))}`,
    token_hash: await sha256Hex(token),
    project_id: projectId,
    created_by: createdBy,
    expires_at: expiresAt,
  })
  if (error) {
    if (/project_share_links/i.test(error.message) && /does not exist|schema cache/i.test(error.message)) {
      throw new Error('Share links need database migration 00542, which has not been applied yet.')
    }
    throw new Error(error.message)
  }
  return { url: `${window.location.origin}/join/${token}`, expiresAt }
}

/** The project's links, newest first (admins only, by RLS). */
export async function listShareLinks(projectId: string): Promise<ShareLink[]> {
  const { data, error } = await supabase
    .from('project_share_links')
    .select('id,created_by,created_at,expires_at,uses,last_used_at,last_used_by')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
  if (error) return []
  return (data ?? []).map((r) => ({
    id: r.id as string,
    createdBy: (r.created_by as string) ?? null,
    createdAt: r.created_at as string,
    expiresAt: r.expires_at as string,
    uses: Number(r.uses ?? 0),
    lastUsedAt: (r.last_used_at as string) ?? null,
    lastUsedBy: (r.last_used_by as string) ?? null,
  }))
}

/**
 * On a /join/<token> address: keep the token for after sign-in and move the
 * address to the app root, so the link never sits in the history or a
 * screenshot. Returns true when an invite was captured.
 */
export function captureShareInvite(loc: { pathname: string } = window.location): boolean {
  const m = JOIN_PATH.exec(loc.pathname)
  if (!m) return false
  try { localStorage.setItem(PENDING_KEY, m[1]!) } catch { /* private window: lost on close */ }
  try { window.history.replaceState(null, '', '/') } catch { /* ignore */ }
  return true
}

export function hasPendingShareInvite(): boolean {
  try { return !!localStorage.getItem(PENDING_KEY) } catch { return false }
}

export interface RedeemResult {
  ok: boolean
  outcome?: 'joined' | 'added' | 'already'
  project?: { id: string; name: string }
  reason?: string
  error?: string
}

/**
 * Redeem the pending invite, if any, for the signed-in person. Clears it once
 * the server has answered (a used, expired or bad link is not retried); keeps
 * it when the server could not be reached. Never throws.
 */
export async function redeemPendingShareInvite(): Promise<RedeemResult | null> {
  let token: string | null = null
  try { token = localStorage.getItem(PENDING_KEY) } catch { return null }
  if (!token) return null
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (!isEntraAuthEnabled()) {
      const { data } = await supabase.auth.getSession()
      const at = data.session?.access_token
      if (!at) return null
      headers.Authorization = `Bearer ${at}`
    }
    const res = await fetch('/api/access/redeem-share', { method: 'POST', headers, body: JSON.stringify({ token }) })
    const body = (await res.json().catch(() => null)) as RedeemResult | null
    if (res.status >= 500 || !body) return null
    try { localStorage.removeItem(PENDING_KEY) } catch { /* ignore */ }
    const result = res.ok ? body : { ok: false, reason: body.reason, error: body.error ?? 'That share link could not be used.' }
    try { sessionStorage.setItem(NOTICE_KEY, JSON.stringify(result)) } catch { /* ignore */ }
    return result
  } catch {
    return null
  }
}

/** The last redeem result in this tab, for the gate to explain an expired or invalid link. */
export function lastShareNotice(): RedeemResult | null {
  try {
    const raw = sessionStorage.getItem(NOTICE_KEY)
    return raw ? (JSON.parse(raw) as RedeemResult) : null
  } catch {
    return null
  }
}
